import { useCallback, useEffect, useState, Fragment } from "react";
import { toast } from "sonner";
import { Users2, Plus, KeyRound, Trash2, Save, X, ShieldCheck, Ban, CheckCircle2, Eye, EyeOff, FileText } from "lucide-react";
import { Card, CardHead, Btn, Badge, Avatar, Spinner, SearchInput } from "@/components/ui-kit";
import api, { API } from "@/lib/api";

const ROLE_TABS = [
  { key: "", label: "All" },
  { key: "doctor", label: "Doctors" },
  { key: "physiotherapist", label: "Clinicians" },
  { key: "ops", label: "Operations" },
  { key: "patient", label: "Patients" },
  { key: "admin", label: "Admins" },
];
const ROLE_TONE = { doctor: "navy", physiotherapist: "gold", patient: "emerald", admin: "indigo", ops: "sky" };
const roleLabel = (r) => (r === "physiotherapist" ? "Clinician" : r === "ops" ? "Operations" : r ? r[0].toUpperCase() + r.slice(1) : "—");

function Field({ label, children }) {
  return <label className="block"><span className="pq-label block mb-1.5">{label}</span>{children}</label>;
}

function CreateModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", role: "doctor", city: "", specialty: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Name and email are required");
    if (f.password && f.password.length < 6) return toast.error("Password must be at least 6 characters");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", f);
      toast.success(`User created — temp password: ${data.temp_password}`, {
        duration: 12000,
        action: {
          label: "Copy",
          onClick: async () => {
            try {
              await navigator.clipboard.writeText(data.temp_password);
              toast.success("Temp password copied — paste it to share with the user");
            } catch {
              toast.error(`Couldn't copy — password is: ${data.temp_password}`);
            }
          },
        },
      });
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create user"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" data-testid="user-create-modal">
      <Card className="w-full max-w-lg my-8">
        <CardHead title="Add User" action={<button onClick={onClose} data-testid="user-create-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>} />
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full name"><input className="pq-input" value={f.name} onChange={set("name")} data-testid="user-create-name" /></Field>
            <Field label="Email"><input className="pq-input" value={f.email} onChange={set("email")} data-testid="user-create-email" /></Field>
            <Field label="Role">
              <select className="pq-input" value={f.role} onChange={set("role")} data-testid="user-create-role">
                <option value="doctor">Doctor</option>
                <option value="physiotherapist">Clinician (Physiotherapist)</option>
                <option value="ops">Operations (PranIQ Ops)</option>
                <option value="patient">Patient</option>
                <option value="admin">Admin</option>
              </select>
            </Field>
            <Field label="Phone"><input className="pq-input" value={f.phone} onChange={set("phone")} data-testid="user-create-phone" /></Field>
            <Field label="Password (optional — assign your own)">
              <div className="relative">
                <input type={showPw ? "text" : "password"} className="pq-input pr-10" value={f.password} onChange={set("password")} placeholder="Leave blank to auto-generate" data-testid="user-create-password" />
                <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="user-create-password-toggle" aria-label={showPw ? "Hide password" : "Show password"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
            {(f.role === "doctor" || f.role === "physiotherapist") && <>
              <Field label="City"><input className="pq-input" value={f.city} onChange={set("city")} data-testid="user-create-city" /></Field>
              <Field label="Specialty"><input className="pq-input" value={f.specialty} onChange={set("specialty")} data-testid="user-create-specialty" /></Field>
            </>}
          </div>
          <p className="text-[12px] text-slate-500">Leave the password blank to auto-generate a temporary one. Either way it's shown once with a Copy button.</p>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
          <Btn onClick={onClose} data-testid="user-create-cancel">Cancel</Btn>
          <Btn variant="primary" onClick={save} disabled={saving} data-testid="user-create-save">{saving ? <Spinner className="w-4 h-4" /> : <><Plus size={15} /> Create user</>}</Btn>
        </div>
      </Card>
    </div>
  );
}

const DOC_LABELS = { photo: "Profile photo", registration: "Medical Council registration", membership: "Membership proof", insurance: "Indemnity insurance", degree: "Degree certificate", id_proof: "ID proof", address_proof: "Address proof" };

function docTone(s) { return s === "Verified" ? "emerald" : s === "Rejected" ? "rose" : "amber"; }

function DocReview({ uid }) {
  const [docs, setDocs] = useState(null);
  useEffect(() => { api.get(`/users/${uid}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([])); }, [uid]);
  const token = localStorage.getItem("praniq_token");
  const fileUrl = (d) => `${API}/documents/${d.id}/file?auth=${token}`;
  if (docs === null) return <div className="text-[12px] text-slate-400 py-2">Loading documents…</div>;
  if (docs.length === 0) return <div className="text-[12px] text-slate-400 py-2" data-testid={`user-docs-empty-${uid}`}>No documents uploaded yet.</div>;
  return (
    <div data-testid={`user-docs-${uid}`}>
      <div className="pq-label mb-2">Uploaded credentials ({docs.length})</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
        {docs.map((d) => {
          const isImg = (d.content_type || "").startsWith("image/");
          return (
            <a key={d.id} href={fileUrl(d)} target="_blank" rel="noreferrer" data-testid={`user-doc-view-${d.id}`}
              className="block rounded-xl border border-slate-200 bg-white p-2 hover:border-navy-500 transition-colors">
              <div className="h-24 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center mb-1.5">
                {isImg ? <img src={fileUrl(d)} alt={DOC_LABELS[d.doc_type] || d.doc_type} className="w-full h-full object-cover" />
                       : <FileText size={26} className="text-slate-400" />}
              </div>
              <div className="text-[11.5px] font-semibold text-slate-700 truncate">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
              <div className="flex items-center justify-between gap-1 mt-0.5">
                <span className="text-[10.5px] text-slate-400 truncate">{d.original_filename}</span>
                <Badge tone={docTone(d.verification_status)}>{d.verification_status}</Badge>
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function EditRow({ u, onSaved }) {
  const [f, setF] = useState({ name: u.name || "", phone: u.phone || "", city: u.city || "", specialty: u.specialty || "" });
  const [pw, setPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => { await api.put(`/admin/users/${u.id}`, f); toast.success("User updated"); onSaved(); };
  const setPassword = async () => {
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    setPwBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, { password: pw });
      toast.success(`Password set for ${data.email}: ${data.temp_password}`, {
        duration: 12000,
        action: { label: "Copy", onClick: async () => { try { await navigator.clipboard.writeText(data.temp_password); toast.success("Password copied"); } catch { toast.error(`Couldn't copy — password is: ${data.temp_password}`); } } },
      });
      setPw("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to set password"); }
    setPwBusy(false);
  };
  return (
    <div className="bg-slate-50/70 border-t border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid={`user-edit-${u.id}`}>
      <Field label="Name"><input className="pq-input py-1.5" value={f.name} onChange={set("name")} data-testid={`user-edit-name-${u.id}`} /></Field>
      <Field label="Phone"><input className="pq-input py-1.5" value={f.phone} onChange={set("phone")} data-testid={`user-edit-phone-${u.id}`} /></Field>
      {(u.role === "doctor" || u.role === "physiotherapist") && <>
        <Field label="City"><input className="pq-input py-1.5" value={f.city} onChange={set("city")} /></Field>
        <Field label="Specialty"><input className="pq-input py-1.5" value={f.specialty} onChange={set("specialty")} /></Field>
      </>}
      <Field label="Assign new password">
        <div className="flex gap-2">
          <input type="text" className="pq-input py-1.5" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Type a password…" data-testid={`user-set-password-${u.id}`} />
          <Btn size="sm" onClick={setPassword} disabled={pwBusy} data-testid={`user-set-password-btn-${u.id}`}>{pwBusy ? <Spinner className="w-3.5 h-3.5" /> : <><KeyRound size={13} /> Set</>}</Btn>
        </div>
      </Field>
      <div className="sm:col-span-2 lg:col-span-4"><Btn size="sm" variant="primary" onClick={save} data-testid={`user-edit-save-${u.id}`}><Save size={13} /> Save changes</Btn></div>
      {(u.role === "doctor" || u.role === "physiotherapist") && (
        <div className="sm:col-span-2 lg:col-span-4 pt-3 mt-1 border-t border-slate-200"><DocReview uid={u.id} /></div>
      )}
    </div>
  );
}

export default function Users() {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => api.get("/admin/users", { params: { role, q } }).then((r) => setData(r.data)), [role, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const verify = async (u, status) => {
    try { await api.put(`/admin/users/${u.id}/verify`, { credential_status: status }); toast.success(`${u.name} → ${status}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update status"); }
  };
  const toggleActive = async (u) => { await api.put(`/admin/users/${u.id}`, { active: !u.active }); toast.success(u.active ? "User suspended" : "User activated"); load(); };
  const reset = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}?`)) return;
    const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
    toast.success(`New temp password for ${data.email}: ${data.temp_password}`, { duration: 10000 });
  };
  const remove = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email})? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not delete user"); }
  };

  if (!data) return <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-navy-600" /></div>;
  const { users, counts } = data;

  return (
    <>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h3 className="font-display font-semibold text-lg text-slate-800 flex items-center gap-2"><Users2 size={19} className="text-navy-600" /> User Control Panel</h3>
          <p className="text-[13px] text-slate-500">Manage every account across all portals — verify, suspend, reset & remove.</p>
        </div>
        <Btn variant="primary" onClick={() => setCreating(true)} data-testid="add-user-btn"><Plus size={15} /> Add user</Btn>
      </div>

      <div className="flex items-center gap-2 flex-wrap mb-4">
        {ROLE_TABS.map((t) => (
          <button key={t.key} onClick={() => setRole(t.key)} data-testid={`user-filter-${t.key || "all"}`}
            className={`pq-chip ${role === t.key ? "pq-chip-active" : ""}`}>
            {t.label} <span className="opacity-70">({counts[t.key || "all"] || 0})</span>
          </button>
        ))}
        <SearchInput testid="user-search" value={q} onChange={setQ} placeholder="Search name or email…" className="sm:w-64 sm:ml-auto" />
      </div>

      <Card>
        <CardHead title="Users" action={<Badge tone="slate">{users.length} shown</Badge>} />
        {users.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">No users match your filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>{["User", "Role", "Status", "Contact", "Actions"].map((h) => <th key={h} className="pq-label px-4 py-3 text-left border-b border-slate-100 whitespace-nowrap">{h}</th>)}</tr></thead>
              <tbody>
                {users.map((u) => (
                  <Fragment key={u.id}>
                    <tr className="border-b border-slate-100 last:border-0 text-[13px] hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={u.name} size={34} />
                          <div className="min-w-0"><div className="font-semibold truncate">{u.name}</div><div className="text-[11.5px] text-slate-400 truncate">{u.email}{u.prq_id ? ` · ${u.prq_id}` : ""}</div></div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><Badge tone={ROLE_TONE[u.role] || "slate"}>{roleLabel(u.role)}</Badge></td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {!u.active ? <Badge tone="rose">Suspended</Badge> : <Badge tone="emerald">Active</Badge>}
                          {(u.role === "doctor" || u.role === "physiotherapist") && <Badge tone={u.credential_status === "Verified" ? "navy" : "amber"}>{u.credential_status || "Pending"}</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{u.phone || u.city || "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          <Btn size="sm" onClick={() => setOpen(open === u.id ? null : u.id)} data-testid={`user-edit-toggle-${u.id}`}>Edit</Btn>
                          {(u.role === "doctor" || u.role === "physiotherapist") && (u.credential_status !== "Verified"
                            ? <Btn size="sm" className="border-emerald-300 text-emerald-700" onClick={() => { if (u.role === "doctor" && !window.confirm(`Override-verify ${u.name}? Any pending documents will be bypassed and their portal fully activated.`)) return; verify(u, "Verified"); }} data-testid={`user-verify-${u.id}`}><ShieldCheck size={13} /> {u.role === "doctor" ? "Verify (override)" : "Verify"}</Btn>
                            : <Btn size="sm" onClick={() => verify(u, "Pending")} data-testid={`user-unverify-${u.id}`}>Unverify</Btn>)}
                          <Btn size="sm" onClick={() => toggleActive(u)} data-testid={`user-toggle-active-${u.id}`}>{u.active ? <><Ban size={13} /> Suspend</> : <><CheckCircle2 size={13} /> Activate</>}</Btn>
                          <Btn size="sm" onClick={() => reset(u)} data-testid={`user-reset-${u.id}`}><KeyRound size={13} /> Reset</Btn>
                          <Btn size="sm" variant="danger" onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}><Trash2 size={13} /></Btn>
                        </div>
                      </td>
                    </tr>
                    {open === u.id && <tr><td colSpan={5} className="p-0"><EditRow u={u} onSaved={() => { setOpen(null); load(); }} /></td></tr>}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && <CreateModal onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </>
  );
}
